/**
 * @fileoverview Protected Route Component
 * @description Route wrapper that requires authentication and optional
 *              module/role-based access control.
 * @module components/auth/ProtectedRoute
 */

import React, { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Paper } from '@mui/material';
import Spinner from '@/components/common/Spinner/Spinner';
import { ShieldX, Send } from 'lucide-react';
import { useAuth } from '@/context';
import './ProtectedRoute.css';

/**
 * Protected Route Component
 * Wraps routes that require authentication and optional RBAC checks.
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to render if authorized
 * @param {string} [props.requiredModule] - Module name required for access
 * @param {string} [props.requiredOperation] - Operation required (default: 'read')
 * @param {string} [props.requiredRole] - Role required for access
 * @returns {JSX.Element} Protected route
 */
export const ProtectedRoute = ({
  children,
  requiredModule,
  requiredOperation = 'read',
  requiredRole,
}) => {
  const { isAuthenticated, isLoading, hasModuleAccess, hasRole, rbacLoaded, isAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Fallback: if RBAC hasn't loaded after 5 seconds, stop blocking
  const [rbacTimedOut, setRbacTimedOut] = useState(false);
  useEffect(() => {
    if (rbacLoaded || isAdmin) return;
    const timer = setTimeout(() => setRbacTimedOut(true), 5000);
    return () => clearTimeout(timer);
  }, [rbacLoaded, isAdmin]);

  // Show loading spinner while checking auth status
  if (isLoading) {
    return (
      <Box className="protected-route__loading">
        <Spinner size={48} />
        <Typography variant="body1" color="text.secondary">
          Checking authentication...
        </Typography>
      </Box>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    // Save the current location to redirect back after login
    sessionStorage.setItem('returnUrl', location.pathname + location.search);
    return <Navigate to="/login" replace />;
  }

  // Wait for RBAC to load before checking permissions (max 5 seconds)
  if ((requiredModule || requiredRole) && !rbacLoaded && !isAdmin && !rbacTimedOut) {
    return (
      <Box className="protected-route__loading">
        <Spinner size={48} />
        <Typography variant="body1" color="text.secondary">
          Loading permissions...
        </Typography>
      </Box>
    );
  }

  // Check role-based access
  if (requiredRole && !hasRole(requiredRole)) {
    return (
      <AccessDenied
        message="You do not have the required role to access this page."
        detail={`Required role: ${requiredRole}`}
        onRequestAccess={() => navigate('/access-request')}
      />
    );
  }

  // Check module-based access
  if (requiredModule && !hasModuleAccess(requiredModule, requiredOperation)) {
    return (
      <AccessDenied
        message="You do not have access to this module."
        detail={`Module: ${requiredModule} | Operation: ${requiredOperation}`}
        onRequestAccess={() => navigate('/access-request')}
      />
    );
  }

  // Render children if authorized
  return children;
};

/**
 * Access Denied view shown when user lacks permissions.
 */
const AccessDenied = ({ message, detail, onRequestAccess }) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '60vh',
      p: 3,
    }}
  >
    <Paper
      elevation={0}
      sx={{
        textAlign: 'center',
        p: 5,
        maxWidth: 480,
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <ShieldX size={64} style={{ color: '#E31837', marginBottom: 16 }} />
      <Typography variant="h5" fontWeight={700} gutterBottom>
        Access Denied
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
        {message}
      </Typography>
      {detail && (
        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mb: 3 }}>
          {detail}
        </Typography>
      )}
      <Button
        variant="contained"
        startIcon={<Send size={16} />}
        onClick={onRequestAccess}
        sx={{ mt: 1 }}
      >
        Request Access
      </Button>
    </Paper>
  </Box>
);

export default ProtectedRoute;
